import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Bot, 
  TrendingUp, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Wallet
} from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useWallet } from '@/context/WalletContext';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency, getRiskColor } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIAnalysis() {
  const { walletAddress, isConnected, connectWallet } = useWallet();
  const { toast } = useToast();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typedFactors, setTypedFactors] = useState([]);

  const runAnalysis = async () => {
    setLoading(true);
    setTypedFactors([]);
    
    try {
      const res = await axios.post(`${API}/ai/credit-analysis`, {
        wallet_address: walletAddress
      });
      setAnalysis(res.data);
      
      // Animate typing effect for factors
      res.data.factors.forEach((factor, i) => {
        setTimeout(() => {
          setTypedFactors(prev => [...prev, factor]);
        }, (i + 1) * 500);
      });
    } catch (err) {
      toast({
        title: 'Analysis Failed',
        description: err.response?.data?.detail || 'Could not complete credit analysis',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runAnalysis();
  }, []);

  const getScoreGrade = (score) => {
    if (score >= 750) return { grade: 'Excellent', color: 'text-green-500' };
    if (score >= 650) return { grade: 'Good', color: 'text-blue-500' };
    if (score >= 550) return { grade: 'Fair', color: 'text-yellow-500' };
    return { grade: 'Poor', color: 'text-red-500' };
  };

  return (
    <DashboardLayout title="AI Credit Analysis">
      <div className="max-w-4xl mx-auto">
        {/* Header Card */}
        <Card className="glass border-white/10 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl gradient-primary flex items-center justify-center">
                  <Bot className="w-7 h-7" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">AI Credit Analysis</h2>
                  <p className="text-muted-foreground">
                    Powered by on-chain data and ML algorithms
                  </p>
                </div>
              </div>
              <Button
                data-testid="refresh-analysis-btn"
                onClick={runAnalysis}
                disabled={loading}
                variant="outline"
                className="border-white/10"
              >
                <RefreshCw className={`mr-2 w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Wallet Connection Prompt */}
        {!isConnected && (
          <Card className="glass border-yellow-500/30 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Wallet className="w-8 h-8 text-yellow-500" />
                <div className="flex-1">
                  <h3 className="font-semibold">Connect Your Wallet</h3>
                  <p className="text-sm text-muted-foreground">
                    Connect your wallet to get a more accurate credit analysis based on your on-chain activity.
                  </p>
                </div>
                <Button onClick={connectWallet} className="gradient-primary">
                  Connect
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading && !analysis ? (
          <Card className="glass border-white/10">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-6 relative">
                <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
                <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Analyzing Your Profile</h3>
              <p className="text-muted-foreground">
                Our AI is processing your data...
              </p>
            </CardContent>
          </Card>
        ) : analysis && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Score Card */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="glass border-white/10">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-6">Credit Score</h3>
                  <div className="flex items-center gap-8">
                    <div className="relative w-40 h-40">
                      <svg className="w-full h-full -rotate-90">
                        <circle
                          cx="80"
                          cy="80"
                          r="70"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="12"
                          className="text-white/10"
                        />
                        <circle
                          cx="80"
                          cy="80"
                          r="70"
                          fill="none"
                          stroke="url(#creditGradient)"
                          strokeWidth="12"
                          strokeLinecap="round"
                          strokeDasharray={`${(analysis.credit_score / 850) * 440} 440`}
                        />
                        <defs>
                          <linearGradient id="creditGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#3B82F6" />
                            <stop offset="100%" stopColor="#8B5CF6" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-4xl font-bold">{analysis.credit_score}</span>
                        <span className={`text-sm ${getScoreGrade(analysis.credit_score).color}`}>
                          {getScoreGrade(analysis.credit_score).grade}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Score Range</span>
                          <span>300 - 850</span>
                        </div>
                        <Progress value={(analysis.credit_score - 300) / 5.5} className="h-2" />
                      </div>
                      <div className={`flex items-center gap-2 ${getRiskColor(analysis.risk_level)}`}>
                        <Shield className="w-5 h-5" />
                        <span className="font-medium">{analysis.risk_level} Risk</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass border-white/10">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-6">Lending Capacity</h3>
                  <div className="space-y-6">
                    <div>
                      <div className="text-sm text-muted-foreground mb-2">Maximum Loan Amount</div>
                      <div className="text-3xl font-bold gradient-text">
                        {formatCurrency(analysis.max_loan_amount)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-2">Recommended Rate Range</div>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-green-500">
                          {analysis.recommended_rate_range.min}%
                        </span>
                        <span className="text-muted-foreground">to</span>
                        <span className="text-2xl font-bold text-yellow-500">
                          {analysis.recommended_rate_range.max}%
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Factors */}
            <Card className="glass border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Analysis Factors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {typedFactors.map((factor, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`flex items-center gap-4 p-4 rounded-xl ${
                        factor.positive ? 'bg-green-500/10' : 'bg-red-500/10'
                      }`}
                    >
                      {factor.positive ? (
                        <CheckCircle className="w-6 h-6 text-green-500 shrink-0" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-500 shrink-0" />
                      )}
                      <div className="flex-1">
                        <span className="font-medium">{factor.factor}</span>
                      </div>
                      <span className={`font-bold ${factor.positive ? 'text-green-500' : 'text-red-500'}`}>
                        {factor.impact}
                      </span>
                    </motion.div>
                  ))}
                  
                  {loading && typedFactors.length < (analysis?.factors?.length || 0) && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      Analyzing...
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* AI Insight */}
            {analysis.ai_insight && (
              <Card className="glass border-blue-500/30">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center shrink-0">
                      <Bot className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
                        AI Insight
                        <span className="text-[10px] bg-blue-500/20 px-2 py-0.5 rounded-full">Gemini 3 Flash</span>
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed" data-testid="ai-insight-text">
                        {analysis.ai_insight}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tips */}
            <Card className="glass border-primary/30">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-primary" />
                  Tips to Improve Your Score
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">
                      Connect your wallet to include on-chain activity in your analysis
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">
                      Stake more KOJIRU tokens to show commitment to the platform
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">
                      Repay loans on time to build positive credit history
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">
                      Maintain consistent on-chain activity across DeFi protocols
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
}
